// src/components/OrganizationTree.js — 組織図 & 緊急連絡管理（タブ切替）
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
//import OrgChart from './OrgChart';
import './OrganizationTree.css';
import { positionRank, topByRank, SUPERVISOR_MIN_RANK } from '../lib/positionRank';

const UNASSIGNED_ID = '__UNASSIGNED__';


// コード：0000/0001/0002 の社員は全カラムに表示
const UNIVERSAL_DEPT_IDS = ['0000', '0001', '0002'];

let _uid = 0;
const nextUid = () => `u${++_uid}`;

/**
 * 課単位として表示するコードか判定
 * - 00xx（先頭 "00"）→ 非表示
 * - xx00（末尾 "00"）→ 非表示
 */
const isVisibleSection = id => {
  if (!id || id.length < 4) return false;
  if (id.startsWith('00')) return false;
  if (id.endsWith('00')) return false;
  return true;
};

/**
 * xx00 パターン（課上位の部署、先頭は "00" ではない）
 * 例: "0100", "0200" → true
 */
const isDivisionLevel = id =>
  Boolean(id && id.length >= 4 && id.endsWith('00') && !id.startsWith('00'));

/** 部署ツリー（API）をフラット化。parent_id はシリアライザで付与 */
const flattenDeptTree = (nodes, parentUuid = null, acc = []) => {
  (nodes || []).forEach(n => {
    acc.push({
      id: n.id,
      departmentId: n.departmentId,
      departmentName: n.departmentName,
      parentId: n.parent_id || parentUuid,
    });
    if (n.children && n.children.length) flattenDeptTree(n.children, n.id, acc);
  });
  return acc;
};

// 組織図と同じランク方式で判定（マネージャー・取締役員などの独自呼称にも対応）
/** リーダー相当（リーダー・主任・係長・課長代理）。課長相当より下位。 */
const isLeaderLikePosition = p => {
  const r = positionRank(p);
  return r >= SUPERVISOR_MIN_RANK && r < 50;
};

function resolveEscalation(subject, pool) {
  if (!subject || !pool || !pool.length) return { leader: null, kacho: null };
  const dept = (subject.departmentName || '').trim();
  const same = pool.filter(
    e => e.status !== 'RESIGNED' && (e.departmentName || '').trim() === dept
  );
  let leader = null;
  const reportTo =
    subject.reports_to_member_id || subject.reportsToMemberId || null;
  if (reportTo) {
    leader = pool.find(e => e.memberId === reportTo) || null;
  }
  if (!leader) {
    leader =
      same.find(e => {
        if (e.memberId === subject.memberId) return false;
        return isLeaderLikePosition(e.position);
      }) || null;
  }
  // 課の最上位者。課長がいなければマネージャー・取締役員なども対象。
  const kacho = topByRank(same, 50) || null;
  return { leader, kacho };
}

function findBuchoForDivision(divisionDeptName, pool) {
  if (!divisionDeptName || !pool) return null;
  const members = (pool || []).filter(
    e => e.status !== 'RESIGNED' && (e.departmentName || '').trim() === divisionDeptName.trim()
  );
  // 部長がいなければ本部長・取締役員など、より上位の人を表示
  return topByRank(members, 60) || null;
}

/**
 * 課内の並び順：
 *  1) 役職ランクが高い順（取締役 → マネージャー → チーフ → リーダー → 一般社員）
 *  2) 同ランク（一般社員など）はコード(memberId)の昇順
 */
const byRankThenCode = (a, b) => {
  const ra = positionRank(a.position);
  const rb = positionRank(b.position);
  if (ra !== rb) return rb - ra;
  return String(a.memberId || '').localeCompare(String(b.memberId || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

/** 課列に対応する課の最上位者（課長／マネージャー／取締役員など） */
function findKachoForSectionColumn(col, pool) {
  if (!col || !pool?.length) return null;
  const deptName = (col.name || '').trim();
  if (!deptName) return null;
  const members = pool.filter(
    e => e.status !== 'RESIGNED' && (e.departmentName || '').trim() === deptName
  );
  return topByRank(members, 50) || null;
}

export default function OrganizationTree() {
  const navigate = useNavigate();

  // タブ: 'org' = 会社組織図 / 'ec' = 緊急連絡管理
  //const [activeTab, setActiveTab] = useState('org');
  const [activeTab, setActiveTab] = useState('ec');
  const [columns, setColumns]     = useState([]);
  const [deptFlat, setDeptFlat]   = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [message, setMessage]     = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [contactModal, setContactModal] = useState(null);
  const [escAttempts, setEscAttempts] = useState(0);
  const dragging = useRef(null);

  // =================== 初期データ取得（fetchInit で共通化） ===================

  // =================== ドラッグ操作 ===================
  const handleDragStart = (e, uid, memberId, sourceColId) => {
    dragging.current = { uid, memberId, sourceColId };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', uid);
  };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCol !== colId) setDragOverCol(colId);
  };

  const handleDragLeave = e => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverCol(null);
  };

  const handleDrop = (e, targetColId) => {
    e.preventDefault();
    setDragOverCol(null);
    const drag = dragging.current;
    if (!drag || drag.sourceColId === targetColId) { dragging.current = null; return; }
    dragging.current = null;

    setColumns(prev => {
      const next = prev.map(col => ({ ...col, employees: [...col.employees] }));
      const src  = next.find(c => c.colId === drag.sourceColId);
      if (!src) return prev;
      const idx = src.employees.findIndex(c => c.uid === drag.uid);
      if (idx === -1) return prev;
      const [card] = src.employees.splice(idx, 1);

      const tgt = next.find(c => c.colId === targetColId);
      if (!tgt) return prev;
      tgt.employees.push({ ...card, uid: nextUid() });
      tgt.employees.sort(byRankThenCode); // 移動後も役職ランク順を保つ
      return next;
    });
  };

  const handleDragEnd = () => {
    dragging.current = null;
    setDragOverCol(null);
  };

  // =================== 複製（section のみ） ===================
  const handleClone = (uid, colId) => {
    setColumns(prev => {
      const next = prev.map(col => ({ ...col, employees: [...col.employees] }));
      const col  = next.find(c => c.colId === colId);
      if (!col) return prev;
      const orig = col.employees.find(c => c.uid === uid);
      if (!orig || orig.cardType !== 'section') return prev;
      col.employees.push({ ...orig, uid: nextUid(), isClone: true });
      return next;
    });
  };

  // =================== 複製カードの削除（誤って複製した時用。複製カードのみ削除可） ===================
  const handleRemoveClone = (uid, colId) => {
    setColumns(prev => prev.map(col =>
      col.colId === colId
        ? { ...col, employees: col.employees.filter(c => !(c.uid === uid && c.isClone)) }
        : col
    ));
  };

  // =================== 保存（BUG-004: 未グループ含め全員の課を送信し、保存後に再取得） ===================
  const fetchInit = useCallback(async () => {
    setLoading(true);
    try {
      const ts = Date.now();
      const [empRes, deptRes] = await Promise.all([
        api.get('/employees', { params: { _t: ts } }),
        api.get('/departments/', { params: { _t: ts } }),
      ]);
      // ページネーション形式 { results: [...] } と配列の両方に対応
      const empData = Array.isArray(empRes.data)
        ? empRes.data
        : (empRes.data?.results ?? []);
      const rawEmps = empData.filter(e => e.status !== 'RESIGNED');
      setAllEmployees(rawEmps);

      const deptRoots = Array.isArray(deptRes.data) ? deptRes.data : (deptRes.data?.results ?? []);
      const flat = flattenDeptTree(deptRoots);
      setDeptFlat(flat);

      const byId = Object.fromEntries(flat.map(d => [d.id, d]));
      const byDeptId = Object.fromEntries(flat.map(d => [d.departmentId, d]));

      const findDivisionForSectionDepId = depId => {
        let cur = byDeptId[depId];
        let guard = 0;
        while (cur && guard++ < 50) {
          if (isDivisionLevel(cur.departmentId)) return cur;
          cur = cur.parentId ? byId[cur.parentId] : null;
        }
        return null;
      };

      const rawDepts = flat;
      const nameToId = {};
      rawDepts.forEach(d => {
        if (d.departmentName) nameToId[d.departmentName] = d.departmentId || String(d.id);
      });
      // ツリー扁平化で同一 departmentId が複数行に出ると列 key が重複するため、課コードで一意化する
      const sectionRows = rawDepts.filter(d => isVisibleSection(d.departmentId));
      const seenDeptCode = new Set();
      const visibleDepts = sectionRows
        .filter(d => {
          const code = d.departmentId || '';
          if (!code || seenDeptCode.has(code)) return false;
          seenDeptCode.add(code);
          return true;
        })
        .sort((a, b) => (a.departmentId || '').localeCompare(b.departmentId || ''));
      const sectionMap = {};
      const unassignedCards = [];
      rawEmps.forEach(emp => {
        const deptId = nameToId[emp.departmentName] || null;
        const card = {
          uid: nextUid(),
          memberId: emp.memberId,
          name: emp.name || emp.memberId || '—',
          position: emp.position || '',
          cardType: 'section',
          isClone: false,
          deptLabel: emp.departmentName || '',
          tel: emp.tel || '',
          email: emp.email || '',
          reportsToMemberId: emp.reports_to_member_id || null,
        };
        if (!deptId) {
          unassignedCards.push(card);
        } else if (isVisibleSection(deptId)) {
          if (!sectionMap[deptId]) sectionMap[deptId] = [];
          sectionMap[deptId].push(card);
        } else {
          unassignedCards.push({ ...card, deptLabel: emp.departmentName || '' });
        }
      });
      // 各課の並び順を「役職ランクが高い順」にする（コード順ではない）
      Object.values(sectionMap).forEach(list => list.sort(byRankThenCode));
      unassignedCards.sort(byRankThenCode);
      const cols = [
        { colId: UNASSIGNED_ID, deptId: null, name: '未グループ', employees: unassignedCards },
        ...visibleDepts.map(dept => ({
          colId: dept.departmentId,
          treeNodeId: dept.id,
          deptId: dept.departmentId,
          name: dept.departmentName || dept.departmentId,
          divisionDeptName: (() => {
            const div = findDivisionForSectionDepId(dept.departmentId);
            return div ? div.departmentName : '';
          })(),
          employees: sectionMap[dept.departmentId] || [],
        })),
      ];
      setColumns(cols);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'データの取得に失敗しました。' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  /**
   * 全課列を「未登録 → 部名（あいうえお）→ 課コード順」に並べ、8列ごとに行分割（1行8課）
   */
  const boardRows = useMemo(() => {
    const un = columns.find(c => c.colId === UNASSIGNED_ID);
    const rest = columns.filter(c => c.colId !== UNASSIGNED_ID);
    const byDiv = new Map();
    rest.forEach(col => {
      const key = (col.divisionDeptName || '').trim() || '__OTHER__';
      if (!byDiv.has(key)) byDiv.set(key, []);
      byDiv.get(key).push(col);
    });
    const divKeys = Array.from(byDiv.keys()).sort((a, b) =>
      a.localeCompare(b, 'ja', { numeric: true })
    );
    const ordered = [];
    if (un) {
      ordered.push({
        ...un,
        _showBucho: false,
        _divisionTitleForCol: '未登録',
      });
    }
    divKeys.forEach(divKey => {
      const cols = (byDiv.get(divKey) || []).slice().sort((a, b) =>
        (a.deptId || '').localeCompare(b.deptId || '', undefined, { numeric: true })
      );
      const title =
        divKey === '__OTHER__' ? 'その他' : divKey;
      cols.forEach(col => {
        ordered.push({
          ...col,
          _showBucho: divKey !== '__OTHER__',
          _divisionTitleForCol: title,
        });
      });
    });
    const rows = [];
    for (let i = 0; i < ordered.length; i += 8) {
      rows.push(ordered.slice(i, i + 8));
    }
    return rows;
  }, [columns]);

  const handleSave = async () => {
    if (!window.confirm(
      '変更を保存しますか？\n移動した社員の所属課（社員マスタ）が更新されます。'
    )) return;

    setSaving(true);
    setMessage(null);
    try {
      const updates = [];
      const seen = new Set();
      // 未グループ（UNASSIGNED_ID）列はスキップ：元の部署をクリアしないため
      columns.forEach(col => {
        if (col.colId === UNASSIGNED_ID) return;
        col.employees.forEach(card => {
          if (card.cardType === 'section' && !card.isClone && !seen.has(card.memberId)) {
            seen.add(card.memberId);
            updates.push({
              memberId: card.memberId,
              departmentId: col.deptId ?? null,
            });
          }
        });
      });

      const res = await api.post('/admin/emergency-contact/save/', { employees: updates });
      const errList = res.data.errors || [];
      if (errList.length > 0) {
        setMessage({
          type: 'error',
          text: `保存しました（${res.data.updated ?? 0} 件更新）。エラー: ${errList.join(' / ')}`,
        });
      } else {
        setMessage({
          type: 'success',
          text: `保存しました（${res.data.updated ?? updates.length} 件更新）`,
        });
      }
      await fetchInit();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || err?.response?.data?.error || '';
      setMessage({ type: 'error', text: `保存に失敗しました。${detail}` });
    } finally {
      setSaving(false);
    }
  };

  // =================== レンダー ===================
  const openContactModal = (card, type, options = {}) => {
    const hideEscalation = Boolean(options.hideEscalation);
    const tel = (card?.tel || card?.phone || '').trim();
    const email = (card?.email || '').trim();
    const subject = allEmployees.find(e => e.memberId === card?.memberId) || {
      memberId: card?.memberId,
      departmentName: card?.deptLabel,
      reports_to_member_id: card?.reportsToMemberId,
    };
    const { leader, kacho } = resolveEscalation(subject, allEmployees);
    setEscAttempts(0);
    setContactModal({
      name: card?.name || card?.memberId || '社員',
      memberId: card?.memberId || '',
      type,
      value: type === 'tel' ? tel : email,
      tel,
      email,
      leader,
      kacho,
      subjectLabel: card?.name || '',
      hideEscalation,
    });
  };

  const closeContactModal = () => {
    setContactModal(null);
    setEscAttempts(0);
  };

  return (
    <div className="ec-page">
      {/* ヘッダー */}
      <div className="ec-header">
        <h2 className="ec-title">緊急連絡網</h2>
        <div className="ec-header-actions">
          <button className="ec-btn-back" onClick={() => navigate('/dashboard')}>
            メニュー画面に戻る
          </button>
          {/* 緊急連絡管理タブ時のみ保存ボタン表示 */}
          {activeTab === 'ec' && (
            <button className="ec-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? '保存中…' : '保存'}
            </button>
          )}
        </div>
      </div>

      {/* タブナビゲーション */}
      {/*}
      <div className="ec-tabs">
        
        <button
          className={`ec-tab${activeTab === 'org' ? ' ec-tab-active' : ''}`}
          onClick={() => setActiveTab('org')}
        >
          会社組織図
        </button>
      
        <button
          className={`ec-tab${activeTab === 'ec' ? ' ec-tab-active' : ''}`}
          onClick={() => setActiveTab('ec')}
        >
          緊急連絡管理
        </button>
            </div>
          */}
      {/* ===== 会社組織図タブ ===== */}
      {/*{activeTab === 'org' && <OrgChart />} */}

      {/* ===== 緊急連絡管理タブ ===== */}
      {activeTab === 'ec' && (
        <>
          {/* 読み込み中 */}
          {loading && <div className="ec-loading">読み込み中…</div>}

          {!loading && (
            <>
              {/* メッセージ */}
              {message && (
                <div className={`ec-message ec-message-${message.type}`}>{message.text}</div>
              )}

              {/* 凡例 */}
              <div className="ec-legend">
                <span className="ec-legend-item ec-legend-section">課員（ドラッグで移動可）</span>
                <span className="ec-legend-item ec-legend-clone">複製カード</span>
                <span className="ec-legend-note">
                  ※ 全社/上位部署（00xx・xx00コード）の社員は「未グループ」に表示されます
                </span>
                <span className="ec-legend-note">
                  ※ 「未グループ」列へ移動した社員は保存対象外です（元の所属課は変わりません）
                </span>
        </div>

              {/* 1行8課のグリッド（未登録→部順→課コード順で並べ、8列で折り返し） */}
              <div className="ec-board-outer">
                {boardRows.map((row, rowIdx) => (
                  <div key={`row-${rowIdx}`} className="ec-board-row">
                    <div className="ec-board-scroll">
                      <div className="ec-board ec-board-eight">
                        {row.map(col => {
                          const bucho =
                            col._showBucho && col.divisionDeptName
                              ? findBuchoForDivision(col.divisionDeptName, allEmployees)
                              : null;
                    
                          const kachoHeader =
                            col.deptId
                              ? findKachoForSectionColumn(col, allEmployees)
                              : null;
                          
                          const buchoCard = bucho
                            ? {
                                name: bucho.name,
                                memberId: bucho.memberId,
                                position: bucho.position || '',
                                tel: bucho.tel || '',
                                email: bucho.email || '',
                                deptLabel: bucho.departmentName,
                                reportsToMemberId:
                                  bucho.reports_to_member_id || bucho.reportsToMemberId,
                              }
                            : null;
                          const kachoCard = kachoHeader
                            ? {
                                name: kachoHeader.name,
                                memberId: kachoHeader.memberId,
                                position: kachoHeader.position || '',
                                tel: kachoHeader.tel || '',
                                email: kachoHeader.email || '',
                                deptLabel: kachoHeader.departmentName,
                                reportsToMemberId:
                                  kachoHeader.reports_to_member_id ||
                                  kachoHeader.reportsToMemberId,
                              }
                            : null;
                          // ヘッダー（部署の責任者・課の責任者）に表示済みの人は、
                          // 下のカード一覧では重複表示しない（保存対象からは外さない）
                          const headerMemberIds = new Set(
                            [bucho?.memberId, kachoHeader?.memberId].filter(Boolean)
                          );
                          const visibleCards = col.employees.filter(
                            c => !headerMemberIds.has(c.memberId)
                          );
                          return (
                            <div
                              key={
                                col.treeNodeId != null
                                  ? `n-${col.treeNodeId}-r${rowIdx}`
                                  : `${col.colId}-r${rowIdx}`
                              }
                              className={`ec-col${dragOverCol === col.colId ? ' ec-col-over' : ''}`}
                              onDragOver={e => handleDragOver(e, col.colId)}
                              onDragLeave={handleDragLeave}
                              onDrop={e => handleDrop(e, col.colId)}
                            >
                              <div className="ec-col-header">
                                <div className="ec-col-division">{col._divisionTitleForCol}</div>
                                {col.colId === UNASSIGNED_ID && (
                                  <div className="ec-col-unassigned-note">
                                    ※この列への移動は保存されません
                                  </div>
                                )}
                                {col._showBucho && col.divisionDeptName ? (
                                  <div className="ec-col-bucho-row">
                                    {buchoCard ? (
                                      <>
                                        <span className="ec-col-bucho-text">
                                          {(buchoCard.position || '部長')}: {buchoCard.name}
                                          {buchoCard.memberId ? `（${buchoCard.memberId}）` : ''}
                                        </span>
                                        <button
                                          type="button"
                                          className="ec-btn-contact"
                                          title={`${buchoCard.position || '部長'}の電話番号を表示`}
                                          onClick={e => {
                                            e.stopPropagation();
                                            openContactModal(buchoCard, 'tel', { hideEscalation: true });
                                          }}
                                        >
                                          📞
                                        </button>
                                        <button
                                          type="button"
                                          className="ec-btn-contact"
                                          title={`${buchoCard.position || '部長'}のメールを表示`}
                                          onClick={e => {
                                            e.stopPropagation();
                                            openContactModal(buchoCard, 'email', {
                                              hideEscalation: true,
                                            });
                                          }}
                                        >
                                          ✉️
                                        </button>
                                      </>
                                    ) : (
                                      <span className="ec-col-bucho-missing">部署の責任者: （マスタ未登録）</span>
                                    )}
                                  </div>
                                ) : null}
                                {kachoHeader && kachoCard ? (
                                  <div className="ec-col-kacho-row">
                                    <span className="ec-col-kacho-text">
                                      {(kachoCard.position || '課長')}: {kachoCard.name}
                                      {kachoCard.memberId ? `（${kachoCard.memberId}）` : ''}
                                    </span>
                                    <button
                                      type="button"
                                      className="ec-btn-contact"
                                      title={`${kachoCard.position || '課長'}の電話番号を表示`}
                                      onClick={e => {
                                        e.stopPropagation();
                                        openContactModal(kachoCard, 'tel', { hideEscalation: true });
                                      }}
                                    >
                                      📞
                                    </button>
                                    <button
                                      type="button"
                                      className="ec-btn-contact"
                                      title={`${kachoCard.position || '課長'}のメールを表示`}
                                      onClick={e => {
                                        e.stopPropagation();
                                        openContactModal(kachoCard, 'email', { hideEscalation: true });
                                      }}
                                    >
                                      ✉️
                                    </button>
                                  </div>
                                ) : null}
                                {kachoHeader && !kachoCard ? (
                                  <div className="ec-col-kacho-row ec-col-kacho-missing">
                                    課の責任者: （マスタ未登録）
                                  </div>
                                ) : null}
                                <div className="ec-col-name">{col.name}</div>
                               {/* {col.deptId && <div className="ec-col-id">{col.deptId}</div>}*/}
                              </div>
                              <div className="ec-col-body">
                                {visibleCards.length === 0 && (
                                  <div className="ec-col-empty">— なし —</div>
                                )}
                                {visibleCards.map(card => (
                                  <div
                                    key={card.uid}
                                    className={[
                                      'ec-card',
                                      'ec-card-section',
                                      'ec-card-draggable',
                                      card.isClone ? 'ec-card-clone' : '',
                                    ].filter(Boolean).join(' ')}
                                    draggable
                                    onDragStart={e =>
                                      handleDragStart(e, card.uid, card.memberId, col.colId)
                                    }
                                    onDragEnd={handleDragEnd}
                                    title={
                                      card.deptLabel
                                        ? `所属: ${card.deptLabel}`
                                        : 'ドラッグして課を変更'
                                    }
                                  >
                                    <button
                                      className="ec-btn-clone"
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleClone(card.uid, col.colId);
                                      }}
                                    >
                                      複製
                                    </button>
                                    {card.isClone && (
                                      <button
                                        type="button"
                                        className="ec-btn-remove-clone"
                                        title="この複製カードを削除"
                                        onClick={e => {
                                          e.stopPropagation();
                                          handleRemoveClone(card.uid, col.colId);
                                        }}
                                      >
                                        ×
                                      </button>
                                    )}
                                    {/* カードは氏名のみ表示（コード・役職名は出さない）。
                                        並び順は「役職ランク降順 → 同ランクはコード昇順」。役職はツールチップで確認可。 */}
                                    <span className="ec-card-name" title={card.position || ''}>
                                      {card.name}
                                    </span>
                                    <button
                                      type="button"
                                      className="ec-btn-contact"
                                      title="電話番号を表示"
                                      onClick={e => {
                                        e.stopPropagation();
                                        openContactModal(card, 'tel');
                                      }}
                                    >
                                      📞
                                    </button>
                                    <button
                                      type="button"
                                      className="ec-btn-contact"
                                      title="メールアドレスを表示"
                                      onClick={e => {
                                        e.stopPropagation();
                                        openContactModal(card, 'email');
                                      }}
                                    >
                                      ✉️
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {contactModal && (
        <div className="ec-contact-modal-overlay" onClick={closeContactModal}>
          <div className="ec-contact-modal" onClick={e => e.stopPropagation()}>
            <h4 className="ec-contact-title">連絡先情報</h4>
            <div className="ec-contact-name">
              {contactModal.name}
              {contactModal.memberId ? `（${contactModal.memberId}）` : ''}
            </div>
            <div className="ec-contact-row">
              <span>電話:</span>
              <strong>{contactModal.tel || '未登録'}</strong>
            </div>
            <div className="ec-contact-row">
              <span>メール:</span>
              <strong>{contactModal.email || '未登録'}</strong>
            </div>
            <div className="ec-contact-row ec-contact-highlight">
              {contactModal.type === 'tel' ? '選択中: 電話番号' : '選択中: メールアドレス'}
              {' / '}
              {contactModal.value || '未登録'}
            </div>

            {!contactModal.hideEscalation ? (
              <div className="ec-contact-escalation">
                <p className="ec-contact-esc-note">
                  緊急時は<strong>まず直属の上司</strong>（リーダー・チーフ・主任・係長など）へ連絡してください。
                  上司へ3回連絡を試みてもつながらない場合は、<strong>課の責任者</strong>（課長・マネージャー・取締役など）へエスカレーションしてください。
                </p>
                {contactModal.leader ? (
                  <div className="ec-contact-tier">
                    <div className="ec-contact-tier-label">1. 上司（優先）</div>
                    <div className="ec-contact-tier-body">
                      <span>{contactModal.leader.name}</span>
                      {contactModal.leader.position ? (
                        <span className="ec-contact-pos">（{contactModal.leader.position}）</span>
                      ) : null}
                      <div className="ec-contact-tier-val">
                        {contactModal.type === 'tel' ? (
                          <>
                            電話:{' '}
                            <strong>{(contactModal.leader.tel || '').trim() || '未登録'}</strong>
                          </>
                        ) : (
                          <>
                            メール:{' '}
                            <strong>{(contactModal.leader.email || '').trim() || '未登録'}</strong>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="ec-contact-esc-warn">
                    上司（リーダー・チーフ等）がマスタから特定できませんでした。課の責任者へ連絡するか、人事にご確認ください。
                  </p>
                )}
                {contactModal.leader ? (
                  <button
                    type="button"
                    className="ec-btn-esc-count"
                    onClick={() => setEscAttempts(a => Math.min(a + 1, 99))}
                  >
                    上司へ連絡を試みた（試行: {escAttempts} 回）
                  </button>
                ) : null}
                {escAttempts >= 3 && contactModal.kacho ? (
                  <div className="ec-contact-tier ec-contact-tier-kacho">
                    <div className="ec-contact-tier-label">2. 課の責任者（3回試行後）</div>
                    <div className="ec-contact-tier-body">
                      <span>{contactModal.kacho.name}</span>
                      {contactModal.kacho.position ? (
                        <span className="ec-contact-pos">（{contactModal.kacho.position}）</span>
                      ) : null}
                      <div className="ec-contact-tier-val">
                        {contactModal.type === 'tel' ? (
                          <>
                            電話:{' '}
                            <strong>{(contactModal.kacho.tel || '').trim() || '未登録'}</strong>
                          </>
                        ) : (
                          <>
                            メール:{' '}
                            <strong>{(contactModal.kacho.email || '').trim() || '未登録'}</strong>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
                {escAttempts >= 3 && !contactModal.kacho ? (
                  <p className="ec-contact-esc-warn">課の責任者が同一課のマスタから見つかりませんでした。</p>
                ) : null}
              </div>
            ) : null}

            <button type="button" className="ec-btn-close" onClick={closeContactModal}>
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
